import React, { useEffect, useMemo, useRef, useState, useCallback, startTransition } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import AnnouncementBar from "../components/app/AnnouncementBar";
import BookOnPitchup from "../components/app/BookOnPitchup";
import Footer from "../components/app/Footer";
import PriceVerifiedPill from "../components/app/PriceVerifiedPill";
import CompareTray from "../components/app/CompareTray";
import PollNudge from "../components/app/PollNudge";
import SignupNudgeModal from "../components/app/SignupNudgeModal";
import InstallPwaBanner from "../components/app/InstallPwaBanner";
import { api, distanceKm, FACILITY_META, formatErr, expandCampsiteCompact } from "../lib/api";
import { DOG_BUCKETS, CAMPFIRE_BUCKETS, SURFACE_BUCKETS, matchesBucket } from "../lib/filterBuckets";
import { useBookingGate } from "../lib/bookingGate";
import { useAuth } from "../context/AuthContext";
import {
  ShowerHead, Bath, Zap, Wifi, ShoppingBag, WashingMachine, Waves,
  Fish, Mountain, TrendingUp, Dog, MapPin, Heart, Star, Search, Loader2, ChevronRight,
  ChevronDown, X as XIcon, Calendar, Users, Flame, Accessibility as AccessibilityIcon,
  Layers, ArrowUpDown, Tent, Globe, Phone, Scale,
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { useWindowVirtualizer } from "@tanstack/react-virtual";

const ICONS = { ShowerHead, Bath, Zap, Wifi, ShoppingBag, WashingMachine, Waves, Fish, Mountain, TrendingUp, Dog };

/**
 * iter83 — Removed `buildCampsitesCoUkUrl()` helper. The DuckDuckGo
 * `!ducky` wrapper worked most of the time but for sites without a
 * Campsites.co.uk listing it dumped users on DuckDuckGo's results page,
 * frequently with a Pitchup AD at the top → competitor commission leak.
 * The Campsites.co.uk button now only appears when a verified deep URL
 * is stored on the site record.
 */

// Per-facility colour tint — improves scannability without overloading
const FACILITY_COLORS = {
  showers: "text-sky-600", toilets: "text-sky-700", electric: "text-amber-500",
  wifi: "text-emerald-600", shop: "text-purple-600", laundry: "text-cyan-600",
  pool: "text-blue-500", swimming: "text-blue-500", fishing: "text-teal-600",
  climbing: "text-stone-600", hiking: "text-lime-700", dog_friendly: "text-orange-600",
};

// Region → marker tint class (matches CSS .r-* classes in index.css)
const REGION_MARKER_CLASS = (region) => {
  const r = (region || "").toLowerCase();
  if (r.includes("cornwall")) return "r-cornwall";
  if (r.includes("devon")) return "r-devon";
  if (r.includes("dorset")) return "r-dorset";
  if (r.includes("new forest")) return "r-newforest";
  if (r.includes("lake district")) return "r-lakedistrict";
  if (r.includes("cumbria")) return "r-cumbria";
  if (r.includes("yorkshire dales")) return "r-yorkshire";
  if (r.includes("yorkshire")) return "r-yorkhumb";
  if (r.includes("peak district")) return "r-peakdistrict";
  if (r.includes("snowdonia")) return "r-snowdonia";
  if (r.includes("pembrokeshire")) return "r-pembs";
  if (r.includes("wales")) return "r-wales";
  if (r.includes("cotswold")) return "r-cotswolds";
  if (r.includes("highland")) return "r-scothighlands";
  if (r.includes("border")) return "r-scotborders";
  if (r.includes("scotland")) return "r-scotland";
  if (r.includes("norfolk")) return "r-norfolk";
  if (r.includes("suffolk") || r.includes("essex")) return "r-suffolk";
  if (r.includes("northumb")) return "r-northumb";
  if (r.includes("midland")) return "r-midlands";
  if (r.includes("kent")) return "r-kent";
  if (r.includes("isle of wight") || r === "iow") return "r-iow";
  if (r.includes("ireland")) return "r-northireland";
  return "";
};

const FACILITY_KEYS = Object.keys(FACILITY_META);

function makeIcon(label, active, regionClass) {
  return L.divIcon({
    className: "",
    html: `<div class="cc-marker ${regionClass || ""} ${active ? "active" : ""}">${label}</div>`,
    iconSize: [32, 32], iconAnchor: [16, 16],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.2));
  }, [points, map]);
  return null;
}

// Strip Leaflet's default "🇺🇦 Leaflet" prefix. The library author ships
// a Ukrainian flag emoji in the default attribution as a solidarity
// gesture — entirely well-intentioned, but a real user mistook it for
// "the map is showing Ukraine" (iter52). Replace with a neutral
// "Map by Leaflet" so we keep proper credit without the flag.
function MapAttributionFix() {
  const map = useMap();
  useEffect(() => {
    try {
      map.attributionControl?.setPrefix('<a href="https://leafletjs.com/" target="_blank" rel="noopener">Map by Leaflet</a>');
    } catch { /* ignore — attribution may be disabled */ }
  }, [map]);
  return null;
}

export default function Browse() {
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial filter state from URL params (shareable filtered URLs)
  const [fromQuery, setFromQuery] = useState("");
  // iter127 — accept lat/lng URL params so the home-page "Near me" toggle
  // can pre-seed fromPoint without needing a geocode round-trip.
  const _initLat = parseFloat(searchParams.get("lat"));
  const _initLng = parseFloat(searchParams.get("lng"));
  const _initialFromPoint = (Number.isFinite(_initLat) && Number.isFinite(_initLng))
    ? { lat: _initLat, lng: _initLng, label: "Near you" }
    : null;
  const [fromPoint, setFromPoint] = useState(_initialFromPoint);
  const [geocoding, setGeocoding] = useState(false);
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [facilities, setFacilities] = useState(
    (searchParams.get("facilities") || "").split(",").filter(Boolean)
  );
  const [maxPrice, setMaxPrice] = useState(Number(searchParams.get("max_price")) || 100);
  const [minPrice, setMinPrice] = useState(Number(searchParams.get("min_price")) || 0);
  const [minRating, setMinRating] = useState(Number(searchParams.get("min_rating")) || 0);
  const [pitch, setPitch] = useState(searchParams.get("pitch") || "any");
  const [radiusKm, setRadiusKm] = useState(Number(searchParams.get("radius")) || 0); // 0 = any
  const [region, setRegion] = useState(
    (searchParams.get("region") || "").split("|").filter(Boolean)
  );
  const [pitchSurface, setPitchSurface] = useState(
    (searchParams.get("pitch_surface") || "").split(",").filter(Boolean)
  );
  const [accessibility, setAccessibility] = useState(
    (searchParams.get("accessibility") || "").split(",").filter(Boolean)
  );
  const [campfires, setCampfires] = useState(
    (searchParams.get("campfires") || "").split(",").filter(Boolean)
  );
  const [dogPolicy, setDogPolicy] = useState(
    (searchParams.get("dog_policy") || "").split(",").filter(Boolean)
  );
  const [openYearRound, setOpenYearRound] = useState(searchParams.get("open_year_round") === "1");
  const [adultsOnly, setAdultsOnly] = useState(searchParams.get("adults_only") === "1");
  const [familyFriendly, setFamilyFriendly] = useState(searchParams.get("family_friendly") === "1");
  const [groupFriendly, setGroupFriendly] = useState(searchParams.get("group_friendly") === "1");
  const [bookableNow, setBookableNow] = useState(searchParams.get("bookable_now") === "1");
  // iter120 — Direct-book filter chip (no platform fees · book direct with owner)
  const [directBook, setDirectBook] = useState(searchParams.get("direct_book") === "1");
  // iter121 — Near-a-fishery filter chip (every campsite with a named fishery within 10 km)
  const [hasFishery, setHasFishery] = useState(searchParams.get("has_fishery") === "1");
  const [sort, setSort] = useState(searchParams.get("sort") || "");

  const [favIds, setFavIds] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const cardRefs = useRef({});
  const { user } = useAuth();
  const navigate = useNavigate();

  // iter51 — IMMEDIATE search.
  //   Previously: `useDeferredValue` intentionally postponed the filter
  //   recompute until the main thread was idle. That kept the input
  //   responsive on slow devices but the LIST + count lagged 100-200 ms
  //   behind every keystroke, which read as "search is slow" to users.
  //   Now: search drives the filter synchronously per keystroke. The
  //   filter is O(N) substring-compare on a pre-lowercased index — ~5 ms
  //   for 11k items on a mid-range phone, well within one paint frame.
  //   Map cluster commit still uses the separate `idleFiltered` debounce
  //   because re-clustering 11k Leaflet markers is still genuinely slow.
  const debouncedSearch = search;

  // Pre-index name + region in lowercase using a side WeakMap keyed by object
  // identity — no full-array spread (saves ~30 MB of allocations on the 11k
  // dataset and ~40 ms of work per `sites` change). The filter below reads
  // these via _nameLower() / _regionLower() helpers so behaviour is unchanged.
  const lowerCache = useRef(new WeakMap());
  // iter94 — pre-lowercased search index per site, cached on a WeakMap
  // keyed by object identity. Stores name + region + a SHORT description
  // slice so multi-token search ("lake dog", "cornwall beach") matches
  // across all three without ever rebuilding the full-list arrays.
  // Description is truncated to 240 chars: keeps the filter fast AND
  // ensures we don't match irrelevant deep-text mid-paragraph noise.
  const _searchIndex = (s) => {
    const m = lowerCache.current;
    let v = m.get(s);
    if (!v) {
      const desc = (s.description || "").toLowerCase().slice(0, 240);
      v = {
        name: (s.name || "").toLowerCase(),
        region: (s.region || "").toLowerCase(),
        desc,
      };
      m.set(s, v);
    }
    return v;
  };
  const _nameLower = (s) => _searchIndex(s).name;
  const _regionLower = (s) => _searchIndex(s).region;
  // `indexed` kept as a stable alias so the filter dep list reads naturally.
  const indexed = sites;

  // Load campsites + regions — two-stage strategy for fast first paint on
  // mobile networks: fetch the first 500 (best-ranked) immediately so the
  // user sees content within ~1 s on 4G, then background-fetch the full
  // 22k catalogue and swap it in silently. Search/filter still works on
  // partial data and gracefully upgrades the moment the rest arrives.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // iter220 — anons only see the curated featured set (~300 sites,
        // rotates every Monday). Signed-in members see the full 22,000+.
        // The signup wall / prompts still fire on interactive actions so
        // conversion pressure is maintained.
        const anonParams = user ? {} : { featured_only: true };
        // Stage 1 — fast: top 200 bookable sites (1st paint < 250ms on mobile)
        const [c, r] = await Promise.all([
          api.get("/campsites", { params: { limit: 200, slim: true, compact: true, ...anonParams } }),
          api.get("/campsites/regions").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        // Expand the compact URLs back to full ones so Stage-1 rows render correctly
        try { for (let i = 0; i < (c.data || []).length; i++) expandCampsiteCompact(c.data[i]); } catch {}
        setSites(c.data);
        setRegions(r.data || []);
        setLoading(false);
        setLoadingMore(true);
      } catch {
        if (!cancelled) setLoading(false);
      }

      // Stage 2 — background full catalogue. Two important UX guards:
      // 1. Delay ~1.5 s after stage 1 so the user gets to interact with
      //    the first 500 BEFORE we kick off the heavy 7 MB JSON download.
      // 2. Wrap setSites in React.startTransition so the resulting
      //    re-render (cluster of 11k markers, filter rerun) is treated
      //    as low-priority — any user input/scroll stays smooth even
      //    while React reconciles the bigger dataset in the background.
      const kickoff = () => {
        if (cancelled) return;
        (async () => {
          try {
            const { data } = await api.get("/campsites", { params: { limit: 30000, slim: true, compact: true, ...(user ? {} : { featured_only: true }) } });
            if (cancelled || !Array.isArray(data) || data.length <= 500) {
              if (!cancelled) setLoadingMore(false);
              return;
            }
            // Expand compact URLs back to full ones (Unsplash + Pitchup)
            for (let i = 0; i < data.length; i++) expandCampsiteCompact(data[i]);
            startTransition(() => setSites(data));
          } catch {} finally {
            if (!cancelled) setLoadingMore(false);
          }
        })();
      };
      // requestIdleCallback if available (Chromium/Firefox), fallback to setTimeout.
      const idle = typeof window !== "undefined" && "requestIdleCallback" in window
        ? (cb) => window.requestIdleCallback(cb, { timeout: 2500 })
        : (cb) => setTimeout(cb, 1500);
      idle(kickoff);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load favorites
  useEffect(() => {
    if (!user) return setFavIds(new Set());
    (async () => {
      try {
        const { data } = await api.get("/favorites");
        setFavIds(new Set(data.map((d) => d.campsite_id)));
      } catch {}
    })();
  }, [user]);

  // Sync URL params (shareable filter state)
  useEffect(() => {
    const params = {};
    if (search) params.q = search;
    if (facilities.length) params.facilities = facilities.join(",");
    if (maxPrice && maxPrice !== 100) params.max_price = maxPrice;
    if (minPrice) params.min_price = minPrice;
    if (minRating) params.min_rating = minRating;
    if (pitch && pitch !== "any") params.pitch = pitch;
    if (radiusKm) params.radius = radiusKm;
    if (region.length) params.region = region.join("|");
    if (pitchSurface.length) params.pitch_surface = pitchSurface.join(",");
    if (accessibility.length) params.accessibility = accessibility.join(",");
    if (campfires.length) params.campfires = campfires.join(",");
    if (dogPolicy.length) params.dog_policy = dogPolicy.join(",");
    if (openYearRound) params.open_year_round = "1";
    if (adultsOnly) params.adults_only = "1";
    if (familyFriendly) params.family_friendly = "1";
    if (groupFriendly) params.group_friendly = "1";
    if (bookableNow) params.bookable_now = "1";
    if (directBook) params.direct_book = "1";
    if (hasFishery) params.has_fishery = "1";
    if (sort) params.sort = sort;
    setSearchParams(params, { replace: true });
  }, [search, facilities, maxPrice, minPrice, minRating, pitch, radiusKm, region,
      pitchSurface, accessibility, campfires, dogPolicy, openYearRound, adultsOnly,
      familyFriendly, groupFriendly, bookableNow, directBook, hasFishery, sort, setSearchParams]);

  const toggleFac = (key) => {
    setFacilities((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };
  const toggleArr = (setter) => (val) => setter((prev) =>
    prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
  );

  // iter128 — auto-geocode a UK-postcode shaped `q` URL param so the home
  // widget can hand the user off here without needing them to retype.
  // Without this, "BB3 2JX" was being applied as a name-substring filter
  // and returning 0 matches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const qParam = searchParams.get("q") || "";
    const alreadyHasPoint = !!fromPoint;
    const looksLikePostcode = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(qParam.trim());
    if (!alreadyHasPoint && looksLikePostcode) {
      (async () => {
        try {
          const { data } = await api.post("/geocode", { query: qParam.trim() });
          if (data && data.length) {
            setFromPoint({ lat: data[0].lat, lng: data[0].lng, label: data[0].display_name });
            setFromQuery(qParam.trim());
            // Clear the name-substring search since we converted it to a point
            setSearch("");
            setSearchParams((sp) => {
              const next = new URLSearchParams(sp);
              next.delete("q");
              next.set("lat", String(data[0].lat));
              next.set("lng", String(data[0].lng));
              return next;
            });
          }
        } catch {}
      })();
    }
  }, []);

  const onGeocode = async (e) => {
    e.preventDefault();
    if (!fromQuery.trim()) return;
    setGeocoding(true);
    try {
      const { data } = await api.post("/geocode", { query: fromQuery });
      if (!data.length) {
        toast.error("Location not found in the UK");
        return;
      }
      setFromPoint({ lat: data[0].lat, lng: data[0].lng, label: data[0].display_name });
      toast.success("Location set");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setGeocoding(false);
    }
  };

  const filtered = useMemo(() => {
    // Single-pass filter with early-exit per site. The previous version chained
    // 10+ `.filter()` calls, each one allocating a new array and walking the
    // entire (15k-site) list. With every check folded into one loop and an
    // early `continue` on first non-match, total work drops from ~10×N to
    // ~N in the typical case — roughly a 5–10× speedup per keystroke.
    //
    // iter94 — MULTI-TOKEN SEARCH. The query is split on whitespace and
    // every token must match name OR region OR description (each token
    // independently). So "lake dog" finds "Lake District" sites tagged
    // dog-friendly without the user typing the full phrase. ~50%
    // reduction in keystrokes to find what people want.
    const rawQ = debouncedSearch.trim().toLowerCase();
    const tokens = rawQ ? rawQ.split(/\s+/).filter((tk) => tk.length > 0) : null;
    const useMaxPrice = maxPrice && maxPrice < 100;
    const useMinPrice = minPrice && minPrice > 0;
    const podName = /pod|glamp/i;
    const podDesc = /camping pod|glamping pod|wooden pod/i;

    const out = [];
    for (let i = 0; i < indexed.length; i++) {
      const s = indexed[i];
      // Multi-token text search — every token must hit name OR region
      // OR (short) description, independently. Fast: 1-2 tokens 99% of
      // the time, indexOf on pre-lowercased strings.
      if (tokens) {
        const idx = _searchIndex(s);
        let allMatched = true;
        for (let ti = 0; ti < tokens.length; ti++) {
          const tk = tokens[ti];
          if (idx.name.indexOf(tk) < 0 && idx.region.indexOf(tk) < 0 && idx.desc.indexOf(tk) < 0) {
            allMatched = false; break;
          }
        }
        if (!allMatched) continue;
      }
      if (facilities.length) {
        const sf = s.facilities;
        if (!sf) continue;
        let allFound = true;
        for (let j = 0; j < facilities.length; j++) {
          if (sf.indexOf(facilities[j]) < 0) { allFound = false; break; }
        }
        if (!allFound) continue;
      }
      // Price filter — only apply when sliders are NOT at their defaults.
      // At the slider's max (£100) we treat it as "no upper bound" so a single
      // higher-priced glamping/peak-season site doesn't silently disappear.
      // For sites with no `price_per_night` yet (the vast majority of the
      // catalogue until crowdsourced reports flow in), we INCLUDE them by
      // default — "max £50" means "exclude sites we can confirm cost more
      // than £50", not "exclude sites whose price we don't know yet".
      // Drops the filter from useless-on-empty-data to actually-useful.
      if (useMaxPrice && s.price_per_night != null && s.price_per_night > maxPrice) continue;
      if (useMinPrice && s.price_per_night != null && s.price_per_night < minPrice) continue;
      if (minRating && s.rating != null && !(s.rating >= minRating)) continue;
      if (pitch !== "any") {
        // Trailer tents: dynamic eligibility (tent && (caravan || campervan))
        // since most existing sites don't have an explicit trailer_tent field.
        // Explicit `false` overrides.
        if (pitch === "trailer_tent") {
          const eligible = s.trailer_tent === false
            ? false
            : (s.trailer_tent || (s.tent && (s.caravan || s.campervan)));
          if (!eligible) continue;
        } else if (pitch === "camping_pod") {
          const eligible = s.camping_pod === false
            ? false
            : (s.camping_pod || podName.test(s.name || "") || podDesc.test(s.description || ""));
          if (!eligible) continue;
        } else if (pitch === "hostel" || pitch === "motel" || pitch === "camp_pitch" || pitch === "guest_house" || pitch === "fishing_lake") {
          // OSM-imported accommodation types — flagged by `osm_tourism` during
          // OSM bulk ingest (iter86/88: tourism=hostel/motel/camp_pitch/guest_house, leisure=fishing+camping=yes).
          if (s.osm_tourism !== pitch) continue;
        } else {
          if (!s[pitch]) continue;
        }
      }
      if (region.length && region.indexOf(s.region) < 0) continue;
      if (accessibility.length) {
        const sa = s.accessibility;
        if (!sa) continue;
        let allFound = true;
        for (let j = 0; j < accessibility.length; j++) {
          if (sa.indexOf(accessibility[j]) < 0) { allFound = false; break; }
        }
        if (!allFound) continue;
      }
      if (campfires.length && !matchesBucket(CAMPFIRE_BUCKETS, campfires, s.campfires)) continue;
      if (dogPolicy.length && !matchesBucket(DOG_BUCKETS, dogPolicy, s.dog_policy)) continue;
      if (pitchSurface.length && !matchesBucket(SURFACE_BUCKETS, pitchSurface, s.pitch_surface)) continue;
      if (openYearRound && s.open_year_round !== true) continue;
      if (adultsOnly && s.adults_only !== true) continue;
      if (familyFriendly && s.family_friendly !== true) continue;
      if (groupFriendly && s.group_friendly !== true) continue;
      // "Bookable now" — we will forward the user to a booking page through
      // ANY of: direct (book_rank=3), verified Pitchup link, owner website,
      // owner phone, or the Campsites.co.uk county-page fallback (which we
      // auto-generate for every regioned site). So every regioned site
      // qualifies, which matches what the user actually experiences.
      if (bookableNow) {
        const bookable = s.book_rank === 3
          || (s.pitchup_listed === true && s.pitchup_url_ok !== false && s.pitchup_url)
          || !!s.website
          || !!s.phone
          || !!s.region;
        if (!bookable) continue;
      }
      // iter120 — Direct-book partners only (no platform fees)
      if (directBook && s.is_direct_booking !== true) continue;
      // iter121 — Near a fishery (within 10 km · OSM-sourced flag)
      if (hasFishery && s.has_nearby_fishery !== true) continue;
      out.push(s);
    }

    let list = out;
    // distance + radius — only when fromPoint is set.
    if (fromPoint) {
      list = list.map((s) => ({ ...s, distance: distanceKm(fromPoint, s) }));
      if (radiusKm) {
        list = list.filter((s) => s.distance != null && s.distance <= radiusKm);
      }
    }

    // sort
    if (sort === "price_asc") list.sort((a, b) => a.price_per_night - b.price_per_night);
    else if (sort === "price_desc") list.sort((a, b) => b.price_per_night - a.price_per_night);
    else if (sort === "rating_desc") list.sort((a, b) => b.rating - a.rating);
    else if (fromPoint) list.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
    else {
      // Default — surface what's bookable RIGHT NOW. Order by signals that
      // mean "user can book today without surprises":
      // 1. pitchup_url_ok !== false → known-working Pitchup link
      // 2. pitchup_listed → 1-tap booking
      // 3. open_year_round → no seasonal closed surprise
      // 4. rating desc, then price asc.
      list.sort((a, b) => {
        const aOk = a.pitchup_url_ok !== false ? 1 : 0;
        const bOk = b.pitchup_url_ok !== false ? 1 : 0;
        if (aOk !== bOk) return bOk - aOk;
        const ap = a.pitchup_listed ? 1 : 0;
        const bp = b.pitchup_listed ? 1 : 0;
        if (ap !== bp) return bp - ap;
        const ay = a.open_year_round ? 1 : 0;
        const by = b.open_year_round ? 1 : 0;
        if (ay !== by) return by - ay;
        if ((b.rating ?? 0) !== (a.rating ?? 0)) return (b.rating ?? 0) - (a.rating ?? 0);
        return (a.price_per_night ?? 0) - (b.price_per_night ?? 0);
      });
    }

    return list;
  }, [indexed, debouncedSearch, facilities, maxPrice, minPrice, minRating, pitch, fromPoint, radiusKm,
      region, pitchSurface, accessibility, campfires, dogPolicy, openYearRound, adultsOnly,
      familyFriendly, groupFriendly, bookableNow, directBook, hasFishery, sort]);

  // iter88: react-virtual now handles windowing — no more visibleCount /
  // IntersectionObserver sentinel. Only viewport-visible rows (~3-4 rows × N
  // columns) get mounted, so 15k→100 filter transitions only reconcile the
  // ~9-12 visible cards instead of 30. Scrolling reveals more rows on demand.
  const columns = useResponsiveColumns();

  // --- Map marker throttle (search-bar responsiveness fix, iter19+iter43) ---
  // Leaflet's MarkerClusterGroup re-clusters 11,104 markers synchronously
  // on every prop change and blocks the main thread for 1-2s. React's
  // useDeferredValue cannot help because the block is non-React Leaflet
  // code. The only effective fix is to prevent the cluster rebuild from
  // running per keystroke. Strategy:
  //   1. While the user is typing (search !== debouncedSearch) pass the
  //      LAST idle snapshot to the map — map stays static during the burst.
  //   2. Once typing stabilises for 700ms, snapshot `filtered` into
  //      `idleFiltered` and let the map re-cluster exactly once.
  //   3. iter43 bumped 400ms → 700ms after profiling showed a 4.8s
  //      mid-type freeze when the cluster commit landed during the next
  //      keystroke. Users barely notice the extra 300ms idle pause; they
  //      strongly notice multi-second mid-type freezes.
  const [idleFiltered, setIdleFiltered] = useState(filtered);
  useEffect(() => {
    const t = setTimeout(() => setIdleFiltered(filtered), 700);
    return () => clearTimeout(t);
  }, [filtered]);
  // Expose the same name the map components already consume
  const mapFiltered = idleFiltered;

  const resetAll = () => {
    setSearch(""); setFacilities([]); setMaxPrice(100); setMinPrice(0); setMinRating(0);
    setPitch("any"); setRadiusKm(0); setRegion([]); setPitchSurface([]); setAccessibility([]);
    setCampfires([]); setDogPolicy([]); setOpenYearRound(false); setAdultsOnly(false);
    setFamilyFriendly(false); setGroupFriendly(false); setBookableNow(false); setDirectBook(false); setHasFishery(false); setSort("");
  };

  const mapPoints = useMemo(() => {
    const pts = mapFiltered
      .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
      .map((s) => ({ lat: s.lat, lng: s.lng }));
    if (fromPoint) pts.push({ lat: fromPoint.lat, lng: fromPoint.lng });
    return pts;
  }, [mapFiltered, fromPoint]);

  // Pre-compute the marker JSX so it's only re-created when the throttled
  // idleFiltered changes — not on every keystroke or card hover. Active-id
  // highlight is intentionally dropped from the dep list so card hovers
  // (which fire `setActiveId` frequently) don't trigger a 6,400-marker
  // rebuild. The active state is still visually communicated by the card
  // background change in the list.
  const markerElements = useMemo(() =>
    mapFiltered
      .filter((s) => typeof s.lat === "number" && typeof s.lng === "number")
      .map((s, i) => (
        <Marker key={s.campsite_id} position={[s.lat, s.lng]}
          icon={makeIcon(String(i + 1), false, REGION_MARKER_CLASS(s.region))}
          eventHandlers={{ click: () => onMarkerClick(s.campsite_id) }}>
          <Popup>
            <div className="text-sm">
              <div className="font-display text-base">{s.name}</div>
              <div className="mono text-xs text-[#555]">
                {typeof s.price_per_night === "number" ? `From £${s.price_per_night.toFixed(2)}/night` : "See on Pitchup"}
                {typeof s.rating === "number" ? ` · ${s.rating.toFixed(1)}★` : ""}
              </div>
              <div className="flex flex-col gap-1 mt-2">
                <Link to={`/campsites/${s.campsite_id}`} className="text-[#ff4500] text-xs underline">View details</Link>
                <BookOnPitchup
                  campsiteId={s.campsite_id}
                  pitchupListed={s.pitchup_listed}
                  pitchupUrl={s.pitchup_url || null}
                  pitchupUrlOk={s.pitchup_url_ok}
                  campsiteName={s.name}
                  campsiteRegion={s.region}
                  websiteUrl={s.website || null}
                  phone={s.phone || null}
                  variant="link"
                  label={s.pitchup_listed === false ? "Find campsite online →" : "Find on Pitchup →"}
                  testId={`book-pitchup-popup-${i}`}
                />
              </div>
            </div>
          </Popup>
        </Marker>
      )),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [mapFiltered]);

  const toggleFavorite = useCallback(async (id) => {
    if (!user) {
      toast.info("Log in to save favorites");
      navigate("/login");
      return;
    }
    try {
      if (favIds.has(id)) {
        await api.delete(`/favorites/${id}`);
        const n = new Set(favIds); n.delete(id); setFavIds(n);
      } else {
        await api.post(`/favorites/${id}`);
        const n = new Set(favIds); n.add(id); setFavIds(n);
      }
    } catch (e) { toast.error(formatErr(e)); }
  }, [user, navigate, favIds]);

  const toggleCompare = useCallback((id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) {
        toast.warning("You can compare up to 4 campsites");
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  const onMarkerClick = (id) => {
    setActiveId(id);
    const el = cardRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const goCompare = () => {
    if (compareIds.length < 2) {
      toast.info("Pick at least 2 campsites to compare");
      return;
    }
    navigate(`/compare?ids=${compareIds.join(",")}`);
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="browse-page">
      <AnnouncementBar />
      <Header active="browse" />

      <div className="flex-1 grid lg:grid-cols-[460px_1fr] lg:min-h-[calc(100vh-64px)] overflow-x-hidden">
        {/* LEFT: Filters + list */}
        {/* iter52: side gutter on mobile (px-3 lg:px-0) so the edges of
            the screen stay scroll-safe, and touchAction: pan-y so range
            sliders don't hijack the vertical scroll when the user just
            wants to move past the filter accordion. */}
        <aside className="border-r hairline flex flex-col lg:max-h-[calc(100vh-64px)] lg:overflow-hidden bg-white max-w-full overflow-x-hidden px-3 lg:px-0 min-w-0" style={{ touchAction: "pan-y" }}>
          <div className="p-6 border-b hairline space-y-4">
            <form onSubmit={onGeocode} className="flex gap-2" data-testid="location-form">
              <input
                className="cc-input"
                placeholder={t("browse.fromPlaceholder")}
                value={fromQuery}
                onChange={(e) => setFromQuery(e.target.value)}
                data-testid="location-input"
              />
              <button type="submit" className="btn-primary !px-4" disabled={geocoding} data-testid="location-submit">
                {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </form>
            {fromPoint && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs mono text-[#555]" data-testid="location-badge">
                  <span className="truncate"><MapPin className="w-3 h-3 inline mr-1" /> {fromPoint.label}</span>
                  <button onClick={() => { setFromPoint(null); setRadiusKm(0); }} className="underline">clear</button>
                </div>
                <div>
                  <div className="eyebrow mb-1">
                    Within · <span className="mono">{radiusKm ? `${radiusKm} km` : "any distance"}</span>
                  </div>
                  <input type="range" min={0} max={300} step={10} value={radiusKm}
                    onChange={(e) => setRadiusKm(Number(e.target.value))} className="w-full accent-[#ff4500]"
                    data-testid="radius-slider" />
                </div>
              </div>
            )}

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
              <input className="cc-input pl-10" placeholder={t("browse.searchPlaceholder")}
                value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" />
            </div>

            {/* "Bookable now" hero filter — book_rank=3 / verified Pitchup link */}
            <button
              type="button"
              onClick={() => setBookableNow((v) => !v)}
              className={`mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 border-2 transition-all ${
                bookableNow
                  ? "border-[#ff4500] bg-[#ff4500] text-white shadow-[0_4px_0_0_#222]"
                  : "border-[#222] bg-white hover:bg-[#fff5ef] text-[#222]"
              }`}
              data-testid="filter-bookable-now"
              aria-pressed={bookableNow}
            >
              <Tent className="w-4 h-4" strokeWidth={2.5} />
              <span className="font-display text-sm tracking-wide">
                {t("browse.bookableNow")}
              </span>
              {bookableNow && (
                <span className="text-[10px] mono opacity-80 ml-1">ON</span>
              )}
            </button>

            {/* Live result count + sort + reset */}
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs mono text-[#555]" data-testid="result-count">
                {loadingMore ? (
                  <span className="inline-flex items-center gap-2" data-testid="loading-more-pill">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#ff4500]" />
                    <span>Loading all campsites…</span>
                    <span className="text-[#888]">({filtered.length.toLocaleString()} so far)</span>
                  </span>
                ) : (
                  <>
                    <strong className="font-display text-base text-[#111]">{filtered.length.toLocaleString()}</strong> {filtered.length === 1 ? t("browse.match") : t("browse.matches")}
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="cc-input !p-1.5 text-xs !w-auto"
                  data-testid="sort-select"
                  aria-label="Sort campsites"
                >
                  <option value="">{fromPoint ? "Closest first" : "Featured · 1-tap book"}</option>
                  <option value="price_asc">Price · low → high</option>
                  <option value="price_desc">Price · high → low</option>
                  <option value="rating_desc">Rating · best first</option>
                </select>
              </div>
            </div>

            {/* Active filter chips */}
            <ActiveChips
              search={search}
              setSearch={setSearch}
              facilities={facilities}
              toggleFac={toggleFac}
              region={region}
              toggleRegion={toggleArr(setRegion)}
              pitch={pitch}
              setPitch={setPitch}
              pitchSurface={pitchSurface}
              togglePitchSurface={toggleArr(setPitchSurface)}
              accessibility={accessibility}
              toggleAccessibility={toggleArr(setAccessibility)}
              campfires={campfires}
              toggleCampfires={toggleArr(setCampfires)}
              dogPolicy={dogPolicy}
              toggleDogPolicy={toggleArr(setDogPolicy)}
              openYearRound={openYearRound}
              setOpenYearRound={setOpenYearRound}
              adultsOnly={adultsOnly}
              setAdultsOnly={setAdultsOnly}
              familyFriendly={familyFriendly}
              setFamilyFriendly={setFamilyFriendly}
              groupFriendly={groupFriendly}
              setGroupFriendly={setGroupFriendly}
              directBook={directBook}
              setDirectBook={setDirectBook}
              hasFishery={hasFishery}
              setHasFishery={setHasFishery}
              minPrice={minPrice}
              setMinPrice={setMinPrice}
              maxPrice={maxPrice}
              setMaxPrice={setMaxPrice}
              minRating={minRating}
              setMinRating={setMinRating}
              radiusKm={radiusKm}
              setRadiusKm={setRadiusKm}
              resetAll={resetAll}
            />
          </div>

          {/* Filter groups — natural flow inside the aside since the sites
              list now lives in its own full-width section below the map. */}
          <div className="px-6 py-4 border-b hairline space-y-4 lg:overflow-y-auto">
            {/* Filter groups */}
            <FilterGroup title="Price & rating" icon={Star} defaultOpen testid="group-price">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="eyebrow mb-2">Min · £<span className="mono">{minPrice}</span></div>
                  <input type="range" min={0} max={100} step={1} value={minPrice}
                    onChange={(e) => setMinPrice(Number(e.target.value))} className="w-full accent-[#ff4500]"
                    data-testid="min-price-slider" />
                </div>
                <div>
                  <div className="eyebrow mb-2">Max · £<span className="mono">{maxPrice}</span></div>
                  <input type="range" min={10} max={100} step={1} value={maxPrice}
                    onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full accent-[#ff4500]"
                    data-testid="max-price-slider" />
                </div>
              </div>
              <div className="mt-4">
                <div className="eyebrow mb-2">Min Rating · <span className="mono">{minRating.toFixed(1)}</span>★</div>
                <input type="range" min={0} max={5} step={0.1} value={minRating}
                  onChange={(e) => setMinRating(Number(e.target.value))} className="w-full accent-[#ff4500]"
                  data-testid="min-rating-slider" />
              </div>
            </FilterGroup>

            <FilterGroup title="Region" icon={MapPin} testid="group-region" badge={region.length || null}>
              <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1">
                {regions.slice(0, 30).map((r) => {
                  const on = region.includes(r.region);
                  return (
                    <button key={r.region} onClick={() => toggleArr(setRegion)(r.region)}
                      className={`px-2.5 py-1.5 text-xs flex items-center gap-1 border ${on ? "bg-[#111] text-white border-[#111]" : "hairline bg-white"}`}
                      data-testid={`region-${r.region.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                      {r.region} <span className="opacity-60 mono text-[10px]">{r.count}</span>
                    </button>
                  );
                })}
              </div>
            </FilterGroup>

            <FilterGroup title="Pitch type & surface" icon={Layers} testid="group-pitch">
              <div className="eyebrow mb-2">Outfit / accommodation</div>
              <div className="flex gap-2 mb-4 flex-wrap">
                {[
                  { id: "any", label: "Any" },
                  { id: "tent", label: "Tent" },
                  { id: "caravan", label: "Caravan" },
                  { id: "campervan", label: "Campervan" },
                  { id: "trailer_tent", label: "Trailer tent" },
                  { id: "camping_pod", label: "Camping pod" },
                  { id: "hostel", label: "Hostel" },
                  { id: "motel", label: "Motel" },
                  { id: "camp_pitch", label: "Camp pitch" },
                  { id: "guest_house", label: "Guest house" },
                ].map((p) => (
                  <button key={p.id} onClick={() => setPitch(p.id)}
                    className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${pitch === p.id ? "bg-[#111] text-white border-[#111]" : "hairline bg-white"}`}
                    data-testid={`pitch-${p.id}`}>{p.label}</button>
                ))}
              </div>
              <div className="eyebrow mb-2">Surface</div>
              <div className="flex gap-2 flex-wrap">
                {["grass", "hardstanding", "gravel"].map((s) => {
                  const on = pitchSurface.includes(s);
                  return (
                    <button key={s} onClick={() => toggleArr(setPitchSurface)(s)}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
                      data-testid={`pitch-surface-${s}`}>{s}</button>
                  );
                })}
              </div>
            </FilterGroup>

            <FilterGroup title="Who's it for" icon={Users} testid="group-who">
              <div className="grid grid-cols-2 gap-2">
                <ToggleChip label="Family-friendly" on={familyFriendly} onClick={() => setFamilyFriendly(v => !v)} testid="filter-family" />
                <ToggleChip label="Adults-only" on={adultsOnly} onClick={() => setAdultsOnly(v => !v)} testid="filter-adults" />
                <ToggleChip label="Group bookings" on={groupFriendly} onClick={() => setGroupFriendly(v => !v)} testid="filter-group" />
                <ToggleChip label="Open year-round" on={openYearRound} onClick={() => setOpenYearRound(v => !v)} testid="filter-year-round" />
                <ToggleChip label="Book direct · no fees" on={directBook} onClick={() => setDirectBook(v => !v)} testid="filter-direct-book" />
                <ToggleChip label="🎣 Near a fishery" on={hasFishery} onClick={() => setHasFishery(v => !v)} testid="filter-has-fishery" />
              </div>
            </FilterGroup>

            <FilterGroup title="Accessibility" icon={AccessibilityIcon} testid="group-accessibility" badge={accessibility.length || null}>
              <div className="flex flex-wrap gap-2">
                {[
                  ["wheelchair_access", "Wheelchair access"],
                  ["accessible_toilets", "Accessible toilets"],
                  ["level_pitches", "Level pitches"],
                ].map(([k, label]) => {
                  const on = accessibility.includes(k);
                  return (
                    <button key={k} onClick={() => toggleArr(setAccessibility)(k)}
                      className={`px-3 py-1.5 text-xs border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
                      data-testid={`accessibility-${k}`}>{label}</button>
                  );
                })}
              </div>
            </FilterGroup>

            <FilterGroup title="Campfires" icon={Flame} testid="group-campfires" badge={campfires.length || null}>
              <div className="flex flex-wrap gap-2">
                {[
                  ["allowed", "Allowed"],
                  ["firepits", "Fire pits provided"],
                  ["none", "No fires"],
                ].map(([k, label]) => {
                  const on = campfires.includes(k);
                  return (
                    <button key={k} onClick={() => toggleArr(setCampfires)(k)}
                      className={`px-3 py-1.5 text-xs border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
                      data-testid={`campfires-${k}`}>{label}</button>
                  );
                })}
              </div>
            </FilterGroup>

            <FilterGroup title="Dogs" icon={Dog} testid="group-dogs" badge={dogPolicy.length || null}>
              <div className="flex flex-wrap gap-2">
                {[
                  ["welcome", "Free for dogs"],
                  ["charged", "Allowed (charged)"],
                  ["off_lead_area", "Off-lead area"],
                  ["none", "No dogs"],
                ].map(([k, label]) => {
                  const on = dogPolicy.includes(k);
                  return (
                    <button key={k} onClick={() => toggleArr(setDogPolicy)(k)}
                      className={`px-3 py-1.5 text-xs border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
                      data-testid={`dog-${k}`}>{label}</button>
                  );
                })}
              </div>
            </FilterGroup>

            <FilterGroup title="Facilities" icon={Wifi} testid="group-facilities" badge={facilities.length || null}>
              <div className="flex flex-wrap gap-2">
                {FACILITY_KEYS.map((key) => {
                  const on = facilities.includes(key);
                  const meta = FACILITY_META[key];
                  const I = ICONS[meta.icon];
                  return (
                    <button key={key} onClick={() => toggleFac(key)}
                      className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
                      data-testid={`facility-${key}`}>
                      <I className="w-3.5 h-3.5" strokeWidth={1.75} /> {meta.label}
                    </button>
                  );
                })}
              </div>
            </FilterGroup>
          </div>

          {/* Compare bar */}
          {compareIds.length > 0 && (
            <div className="px-6 py-3 bg-[#111] text-white flex items-center justify-between" data-testid="compare-bar">
              <div className="text-xs tracking-wider uppercase">{compareIds.length} selected</div>
              <div className="flex gap-2">
                <button onClick={() => setCompareIds([])} className="text-xs underline">clear</button>
                <button onClick={goCompare} className="text-xs bg-[#ff4500] px-3 py-1.5 tracking-wider uppercase font-bold flex items-center gap-1" data-testid="go-compare">
                  Compare <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}
        </aside>

        {/* RIGHT: Map */}
        {/* iter52: shrink mobile height (60vh → 42vh) so there's less
            scroll-trap surface, add side gutter (px-3 lg:px-0) so vertical
            scrolling has a "safe zone" outside the map, and force
            touch-action: pan-y so the OS routes vertical-dominant
            gestures to the page even if the finger lands inside the map. */}
        <div className="relative h-[42vh] lg:h-auto px-3 lg:px-0" style={{ isolation: "isolate", touchAction: "pan-y" }}>
          <div className="relative h-full overflow-hidden border hairline lg:border-0">
            <MapContainer center={[54.5, -3]} zoom={6} className="h-full w-full lg:!h-[calc(100vh-64px)]" style={{ zIndex: 0 }} scrollWheelZoom>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; OSM'
              />
              <FitBounds points={mapPoints} />
              <MapAttributionFix />
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={60}
                spiderfyOnMaxZoom
                showCoverageOnHover={false}
                disableClusteringAtZoom={12}
              >
              {markerElements}
              </MarkerClusterGroup>
              {fromPoint && (
                <Marker position={[fromPoint.lat, fromPoint.lng]} icon={L.divIcon({
                  className: "",
                  html: `<div class="cc-marker" style="background:#111;color:#ff4500;border-color:#ff4500;">●</div>`,
                  iconSize: [32, 32], iconAnchor: [16, 16],
                })}>
                  <Popup>Your starting point</Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* SITES LIST — virtualized window-scroll grid below the map. Cards
          tile into 1/2/3 columns based on screen size so they breathe and the
          filters on the left never compete with them for space. */}
      <section className="border-t hairline bg-white" data-testid="sites-list">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#888]">{t("browse.noMatches")}</div>
        ) : (
          <>
            <VirtualizedSiteList
              items={filtered}
              columns={columns}
              favIds={favIds}
              compareIds={compareIds}
              activeId={activeId}
              setActiveId={setActiveId}
              toggleFavorite={toggleFavorite}
              toggleCompare={toggleCompare}
              cardRefs={cardRefs}
            />
            <div className="px-6 py-4 border-t hairline text-center text-xs mono text-[#666] bg-[#fafafa]" data-testid="list-footer">
              <span>All <span className="font-display text-base text-[#111]">{filtered.length.toLocaleString()}</span> shown</span>
            </div>
          </>
        )}
      </section>
      <CompareTray
        count={compareIds.length}
        onGo={goCompare}
        onClear={() => setCompareIds([])}
      />
      <PollNudge />
      <Footer />
    </div>
  );
}

// --- Helper components ---

// Memoized site card. Critical perf: with 100+ visible cards, re-rendering
// every one on every parent state change (hover, search keystroke, scroll)
// blocks the main thread. React.memo + stable callback refs lets cards
// stay mounted and untouched between renders unless their own data changes.
const SiteCard = React.memo(function SiteCard({ s, i, isFav, isCmp, isActive, onActivate, onFavorite, onCompare, cardRefs }) {
  const gate = useBookingGate();
  return (
    <article
      ref={(el) => (cardRefs.current[s.campsite_id] = el)}
      onMouseEnter={() => onActivate(s.campsite_id)}
      className={`flex gap-4 p-5 border-b hairline min-w-0 ${isActive ? "bg-[#ffefea]" : ""}`}
      data-testid={`site-card-${i}`}
    >
      <div className="w-28 h-28 shrink-0 bg-gradient-to-br from-[#ff6a2c] to-[#e63a00] flex items-center justify-center text-white relative overflow-hidden">
        <img
          src={s.image}
          alt=""
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
        <Tent className="w-8 h-8 opacity-90 relative" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/campsites/${s.campsite_id}`} className="font-display text-lg leading-tight hover:text-[#ff4500]" data-testid={`site-title-${i}`}>
            {s.name}
          </Link>
          <button onClick={() => onFavorite(s.campsite_id)} aria-label="favorite" data-testid={`fav-btn-${i}`}>
            <Heart className={`w-4 h-4 ${isFav ? "fill-[#ff4500] text-[#ff4500]" : "text-[#555]"}`} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => onCompare && onCompare(s.campsite_id)}
            aria-label="Add to comparison"
            aria-pressed={!!isCmp}
            data-testid={`compare-btn-${i}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] mono uppercase tracking-wider font-bold border transition-colors shrink-0 ${
              isCmp
                ? "bg-[#ff4500] text-white border-[#ff4500]"
                : "bg-white text-[#555] border-[#d4d4d4] hover:border-[#ff4500] hover:text-[#ff4500]"
            }`}
          >
            <Scale className="w-2.5 h-2.5" />
            {isCmp ? "Picked" : "Compare"}
          </button>
        </div>
        <div className="text-xs mono text-[#555] mt-1 truncate">{s.region}</div>
        <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
          <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" /><span className="mono">{(s.rating ?? 0).toFixed(1)}</span></span>
          <span className="mono">{typeof s.price_per_night === "number" ? <>From £{s.price_per_night.toFixed(2)}<span className="text-[#888]">/nt</span></> : <span className="text-[#888]">See on Pitchup</span>}</span>
          <PriceVerifiedPill site={s} className="!text-[10px] !px-1.5 !py-0.5" />
          {s.distance != null && <span className="mono text-[#555]">{s.distance.toFixed(0)} km</span>}
          {(() => {
            const hasPitchup = s.pitchup_url_ok === true && s.pitchup_url;
            const hasWebsite = !!s.website;
            const hasPhone = !!s.phone;
            const bookable = hasPitchup || hasWebsite || hasPhone;
            if (!bookable) return null;
            const channels = [];
            if (hasPitchup) channels.push("Pitchup");
            if (hasWebsite) channels.push("website");
            if (hasPhone) channels.push("phone");
            return (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 mono"
                title={`Bookable via ${channels.join(" / ")}`}
                data-testid={`badge-bookable-${i}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                Bookable
              </span>
            );
          })()}
          {s.is_direct_booking && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#fff4ee] text-[#7a2400] border border-[#ff4500]/40 mono"
              title="Book direct with the owner — no platform fees"
              data-testid={`badge-direct-book-${i}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#ff4500]"></span>
              Book direct
            </span>
          )}
          {s.open_year_round && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`badge-year-round-${i}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Year-round
            </span>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-1.5 flex-wrap">
            {(s.facilities || []).slice(0, 4).map((f) => {
              const I = ICONS[FACILITY_META[f]?.icon];
              const tint = FACILITY_COLORS[f] || "text-[#555]";
              return I ? <I key={f} className={`w-3.5 h-3.5 ${tint}`} strokeWidth={1.75} /> : null;
            })}
          </div>
          <label className="text-[11px] uppercase tracking-wider mono flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={isCmp} onChange={() => onCompare(s.campsite_id)} data-testid={`compare-check-${i}`} /> compare
          </label>
        </div>
        <div className="mt-3">
          {/* Secondary booking chips — Pitchup + Direct + Call.
              iter83: removed the auto-generated Campsites.co.uk hero CTA
              because the DuckDuckGo !ducky wrapper sometimes lands users
              on a search-results page with a PITCHUP AD on top, burning
              commission to a direct competitor. Will return as a primary
              CTA when we have verified deep URLs (admin-curated or
              affiliate API ingest).
              Icon-only on phones (text gets truncated at narrow widths),
              icon + label on tablets+. Title attribute carries the full
              label so long-press / hover always shows it. */}
          <div className="grid grid-cols-3 gap-1.5 text-[10px] mono uppercase tracking-wider">
            <a
              href={s.pitchup_url_ok && s.pitchup_url ? s.pitchup_url : `https://www.pitchup.com/search?searchstring=${encodeURIComponent((s.name || "") + " " + (s.region || ""))}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => gate(e, { reason: "book this campsite on Pitchup" })}
              className="inline-flex items-center justify-center gap-1 px-2 py-2 bg-[#ff4500] hover:bg-[#e53e00] text-white font-bold transition-colors"
              data-testid={`book-pitchup-list-${i}`}
              title="Find on Pitchup"
            >
              <Tent className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
              <span className="hidden sm:inline truncate">Pitchup</span>
            </a>
            {s.website ? (
              <a
                href={s.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => gate(e, { reason: "book this campsite direct" })}
                className="inline-flex items-center justify-center gap-1 px-2 py-2 bg-[#1f5fbf] hover:bg-[#194f9b] text-white font-bold transition-colors"
                data-testid={`book-direct-list-${i}`}
                title="Book direct on campsite's own website"
              >
                <Globe className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
                <span className="hidden sm:inline truncate">Direct</span>
              </a>
            ) : (
              <span className="inline-flex items-center justify-center px-2 py-2 bg-[#f3f3f3] text-[#bbb] font-bold cursor-not-allowed" title="No website on record">
                <Globe className="w-3.5 h-3.5 shrink-0 opacity-60" strokeWidth={2.25} />
              </span>
            )}
            {s.phone ? (
              <a
                href={`tel:${String(s.phone).replace(/[^+\d]/g, "")}`}
                onClick={(e) => gate(e, { reason: "call this campsite" })}
                className="inline-flex items-center justify-center gap-1 px-2 py-2 bg-[#1f7a35] hover:bg-[#196a2d] text-white font-bold transition-colors"
                data-testid={`book-call-list-${i}`}
                title={`Call ${s.name} direct`}
              >
                <Phone className="w-3.5 h-3.5 shrink-0" strokeWidth={2.25} />
                <span className="hidden sm:inline truncate">Call</span>
              </a>
            ) : (
              <span className="inline-flex items-center justify-center px-2 py-2 bg-[#f3f3f3] text-[#bbb] font-bold cursor-not-allowed" title="No phone on record">
                <Phone className="w-3.5 h-3.5 shrink-0 opacity-60" strokeWidth={2.25} />
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}, (prev, next) => {
  // Re-render only when this card's own state changes. Activating a
  // sibling card (setActiveId on another row) must NOT cause this card
  // to re-render — that was the scroll/search lag bottleneck.
  return (
    prev.s === next.s &&
    prev.i === next.i &&
    prev.isFav === next.isFav &&
    prev.isCmp === next.isCmp &&
    prev.isActive === next.isActive
  );
});

function FilterGroup({ title, icon: Icon, children, defaultOpen = false, badge, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border hairline" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-[#fafafa]"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold">
          {Icon && <Icon className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={1.75} />} {title}
          {badge ? (
            <span className="ml-1 mono text-[10px] bg-[#ff4500] text-white px-1.5 py-0.5">
              {badge}
            </span>
          ) : null}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#555] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  );
}

function ToggleChip({ label, on, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-xs border text-left ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "hairline bg-white"}`}
      data-testid={testid}
      aria-pressed={on}
    >
      {label}
    </button>
  );
}

function ActiveChips(p) {
  const chips = [];
  if (p.search) chips.push({ key: "search", label: `"${p.search}"`, clear: () => p.setSearch("") });
  if (p.minPrice) chips.push({ key: "minp", label: `Min £${p.minPrice}`, clear: () => p.setMinPrice(0) });
  if (p.maxPrice && p.maxPrice !== 100) chips.push({ key: "maxp", label: `Max £${p.maxPrice}`, clear: () => p.setMaxPrice(100) });
  if (p.minRating) chips.push({ key: "rate", label: `${p.minRating.toFixed(1)}★+`, clear: () => p.setMinRating(0) });
  if (p.radiusKm) chips.push({ key: "rad", label: `Within ${p.radiusKm}km`, clear: () => p.setRadiusKm(0) });
  if (p.pitch && p.pitch !== "any") chips.push({ key: "pitch", label: p.pitch, clear: () => p.setPitch("any") });
  p.region.forEach((r) => chips.push({ key: `r-${r}`, label: r, clear: () => p.toggleRegion(r) }));
  p.pitchSurface.forEach((s) => chips.push({ key: `ps-${s}`, label: s, clear: () => p.togglePitchSurface(s) }));
  p.accessibility.forEach((a) => chips.push({ key: `acc-${a}`, label: a.replace(/_/g, " "), clear: () => p.toggleAccessibility(a) }));
  p.campfires.forEach((c) => chips.push({ key: `cf-${c}`, label: `Fires: ${c}`, clear: () => p.toggleCampfires(c) }));
  p.dogPolicy.forEach((d) => chips.push({ key: `dog-${d}`, label: `Dogs: ${d.replace(/_/g, " ")}`, clear: () => p.toggleDogPolicy(d) }));
  p.facilities.forEach((f) => chips.push({ key: `f-${f}`, label: f, clear: () => p.toggleFac(f) }));
  if (p.openYearRound) chips.push({ key: "yr", label: "Open year-round", clear: () => p.setOpenYearRound(false) });
  if (p.adultsOnly) chips.push({ key: "ad", label: "Adults-only", clear: () => p.setAdultsOnly(false) });
  if (p.familyFriendly) chips.push({ key: "fam", label: "Family", clear: () => p.setFamilyFriendly(false) });
  if (p.groupFriendly) chips.push({ key: "grp", label: "Group", clear: () => p.setGroupFriendly(false) });
  if (p.directBook) chips.push({ key: "direct", label: "Book direct · no fees", clear: () => p.setDirectBook(false) });
  if (p.hasFishery) chips.push({ key: "fishery", label: "🎣 Near a fishery", clear: () => p.setHasFishery(false) });

  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="active-filter-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          onClick={c.clear}
          className="inline-flex items-center gap-1 text-[11px] mono bg-[#fff4ee] border border-[#ff4500]/40 text-[#7a2400] px-2 py-1 hover:bg-[#ff4500] hover:text-white"
          data-testid={`active-chip-${c.key}`}
        >
          {c.label} <XIcon className="w-3 h-3" />
        </button>
      ))}
      <button
        onClick={p.resetAll}
        className="text-[11px] mono uppercase tracking-wider underline text-[#555] hover:text-[#ff4500] ml-1"
        data-testid="reset-filters"
      >
        Reset all
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// VirtualizedSiteList — window-scroll virtualization via @tanstack/react-virtual.
//
// Previously: 30 cards always mounted + IntersectionObserver lazy-loaded
// 30 more on scroll. A 15k→100 filter transition meant unmounting 30 cards
// and mounting 30 new ones — ~60-90ms of React reconciliation + DOM work,
// surfacing as ~280-320ms peaks on mid-range phones (iter51 backlog item).
//
// Now: only the cards inside the viewport (+overscan) are mounted. A typical
// 1280px desktop viewport fits ~3-4 rows of 3 cards = 9-12 mounted. On
// mobile ~3-4 cards. Filter transitions become near-instant because only
// the visible cards need keyed reconciliation; everything off-screen is a
// cheap spacer div.
//
// Uses `useWindowVirtualizer` so the existing page scroll behaviour is
// preserved (sticky header, footer below list, etc.) — no nested
// scroll container, no layout disruption.
// ---------------------------------------------------------------------------
function useResponsiveColumns() {
  // Tailwind breakpoints used in the existing grid: md=768 (2 cols), xl=1280 (3 cols).
  const compute = () => {
    if (typeof window === "undefined") return 1;
    const w = window.innerWidth;
    if (w >= 1280) return 3;
    if (w >= 768) return 2;
    return 1;
  };
  const [cols, setCols] = useState(compute);
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setCols(compute()));
    };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); cancelAnimationFrame(raf); };
  }, []);
  return cols;
}

function VirtualizedSiteList({
  items, columns, favIds, compareIds, activeId, setActiveId,
  toggleFavorite, toggleCompare, cardRefs,
}) {
  // Group flat items into rows of `columns` length so each virtualized row
  // renders the right number of side-by-side cards.
  const rows = useMemo(() => {
    const out = [];
    for (let i = 0; i < items.length; i += columns) {
      out.push(items.slice(i, i + columns));
    }
    return out;
  }, [items, columns]);

  const listRef = useRef(null);
  const [listOffset, setListOffset] = useState(0);
  // Track the list's top offset relative to the document so the virtualizer
  // knows where the viewport starts. Re-measured on resize + when items change
  // (e.g. result count changes the row count, shifting the list).
  useEffect(() => {
    const measure = () => {
      if (!listRef.current) return;
      const rect = listRef.current.getBoundingClientRect();
      setListOffset(rect.top + window.scrollY);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [rows.length, columns]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    // Estimated row height — actual card height varies with badges/wrap.
    // react-virtual auto-measures the real height on first render and
    // adjusts subsequent rows. Initial estimate of 320px keeps the early
    // scroll target accurate within a few cards.
    estimateSize: () => 320,
    overscan: 3,
    scrollMargin: listOffset,
  });

  const virtualRows = virtualizer.getVirtualItems();

  return (
    <div
      ref={listRef}
      style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative", width: "100%" }}
      data-testid="virtual-list-root"
    >
      {virtualRows.map((vr) => {
        const rowCards = rows[vr.index];
        if (!rowCards) return null;
        return (
          <div
            key={vr.key}
            data-index={vr.index}
            ref={virtualizer.measureElement}
            className={`grid grid-cols-1 ${columns >= 2 ? "md:grid-cols-2" : ""} ${columns >= 3 ? "xl:grid-cols-3" : ""}`}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${vr.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            {rowCards.map((s, j) => (
              <SiteCard
                key={s.campsite_id}
                s={s}
                i={vr.index * columns + j}
                isFav={favIds.has(s.campsite_id)}
                isCmp={compareIds.includes(s.campsite_id)}
                isActive={activeId === s.campsite_id}
                onActivate={setActiveId}
                onFavorite={toggleFavorite}
                onCompare={toggleCompare}
                cardRefs={cardRefs}
              />
            ))}
          </div>
        );
      })}
      <InstallPwaBanner />
      <SignupNudgeModal />
    </div>
  );
}
